import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { fetchAdminLawyers, verifyLawyer, updateLawyer, lawyerAccountLifecycle } from "@admin/services/adminApi";
import { PageHeader, DataTable, Pagination, FilterBar, Pill } from "@admin/components/Primitives";
import { ADMIN_BASE } from "@admin/constants/route";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ListingActions } from "@admin/components/lawyers/ListingActions";
import { LifecycleButton } from "@admin/components/lawyers/LifecycleButton";
import { AvailabilityToggle } from "@admin/components/lawyers/AvailabilityToggle";
import { VlaHeader, VlaStatus } from "@admin/components/lawyers/VlaStatus";
import { ActionsMenu } from "@admin/components/lawyers/ActionsMenu";
import { formatLawyerDisplayName } from "@admin/lib/lawyerDisplayName";

const LIMIT = 20;

export default function AdminLawyers() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [verified, setVerified] = useState("all");
  const [listing, setListing] = useState("all");
  const [account, setAccount] = useState("exclude_deleted");
  const [level, setLevel] = useState("any");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchAdminLawyers({ q: q || undefined, verified, listing, account, level, skip, limit: LIMIT });
      setData(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load lawyers"); }
    setLoading(false);
  }, [q, verified, listing, account, level, skip]);

  useEffect(() => { load(); }, [load]);

  const toggleVerified = async (l) => {
    const reason = window.prompt(
      `${!l.verified ? "Verify" : "Un-verify"} ${formatLawyerDisplayName(l.name)} — this is an exceptional admin override. Reason (min 10 characters):`
    );
    if (!reason || reason.trim().length < 10) { if (reason !== null) toast.error("Reason must be at least 10 characters"); return; }
    try {
      await verifyLawyer(l.id, !l.verified, reason.trim());
      toast.success(`${formatLawyerDisplayName(l.name)} ${!l.verified ? "verified (override)" : "un-verified — listing revoked"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleAvailable = async (l) => {
    try {
      await updateLawyer(l.id, { available: !l.available });
      toast.success(`${formatLawyerDisplayName(l.name)} marked ${!l.available ? "online" : "offline"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const lifecycle = async (r, op) => {
    const labels = { disable: "Disable", reenable: "Re-enable", suspend: "SUSPEND (only an admin can lift this)", delete: "DELETE (irreversible for the lawyer)" };
    const reason = window.prompt(`${labels[op]} account for ${formatLawyerDisplayName(r.name)}?\nEnter a reason (required):`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required."); return; }
    try {
      const res = await lawyerAccountLifecycle(r.id, op, reason.trim());
      setData((prev) => ({ ...prev, items: prev.items.map((x) => x.id === r.id
        ? { ...x, account_status: res.account_status, ...(res.account_status === "deleted" ? { name: "Deleted account" } : {}) } : x) }));
      toast.success(`Account ${op === "reenable" ? "re-enabled" : op + "d"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const columns = [
    {
      key: "name",
      label: "Lawyer",
      render: (r) => (
        <div className="min-w-[200px]">
          <a href={`${ADMIN_BASE}/lawyers/${r.id}/review`} data-testid={`lawyer-name-link-${r.id}`}
             aria-label={`Open review profile for ${formatLawyerDisplayName(r.name, "lawyer")}`}
             className="font-bold text-[#0A2540] underline decoration-transparent underline-offset-4 hover:decoration-[#D4AF37] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] rounded transition-colors">
            {formatLawyerDisplayName(r.name, "—")}
          </a>
          <p className="text-xs text-slate-500">{r.specialization} · {r.city}</p>
        </div>
      ),
    },
    { key: "email", label: "Email", render: (r) => <span className="text-xs text-slate-500">{r.email}</span> },
    {
      key: "level",
      label: "Level",
      render: (r) => {
        const lvl = r.pricing_level?.active_level;
        return (
          <a href={`${ADMIN_BASE}/lawyers/${r.id}/review#section-pricing-level`} data-testid={`lawyer-level-${r.id}`}
             data-state={lvl ? "assigned" : "unassigned"}
             title={lvl ? `Level ${lvl} · base fee ₹${Math.round((r.pricing_level.active_base_fee_paise || 0) / 100).toLocaleString("en-IN")}` : "No level assigned — open the profile to assign one"}
             className="inline-block rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]">
            {lvl ? <Pill color="blue">L{lvl}</Pill> : <Pill color="amber">Unassigned</Pill>}
          </a>
        );
      },
    },
    {
      key: "fee",
      label: "Base fee",
      render: (r) => {
        const paise = r.pricing_level?.active_level ? r.pricing_level.active_base_fee_paise : null;
        return (
          <span data-testid={`lawyer-fee-${r.id}`} title={paise != null ? "Base fee (video · 15 min) from the assigned level" : "Fees come from the level — assign one to set the fee"}
                className={`text-sm font-bold ${paise != null ? "text-[#0A2540]" : "text-slate-400"}`}>
            {paise != null ? `₹${Math.round(paise / 100).toLocaleString("en-IN")}` : "—"}
          </span>
        );
      },
    },
    { key: "rating", label: "Rating", render: (r) => <span className="font-bold">★ {Number(r.rating || 0).toFixed(1)}</span> },
    {
      key: "vla",
      label: <VlaHeader />,
      render: (r) => <div data-testid={`lawyer-state-${r.id}`}><VlaStatus lawyer={r} /></div>,
    },
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <ActionsMenu lawyer={r}>
          {({ onDialogOpenChange }) => (
            <>
              <button onClick={() => toggleVerified(r)} className="px-2 py-1 text-xs font-bold rounded-md border border-slate-200 hover:border-[#0A2540]" data-testid={`verify-toggle-${r.id}`}>
                {r.verified ? "Unverify" : "Verify"}
              </button>
              <ListingActions lawyer={r} onChanged={load} onDialogOpenChange={onDialogOpenChange} />
              <AvailabilityToggle lawyer={r} onToggle={toggleAvailable} />
              {(r.account_status || "active") !== "deleted" && (
                (r.account_status || "active") === "active" ? (
                  <>
                    <LifecycleButton op="disable" onClick={() => lifecycle(r, "disable")} testid={`lawyer-account-disable-${r.id}`} />
                    <LifecycleButton op="suspend" onClick={() => lifecycle(r, "suspend")} testid={`lawyer-account-suspend-${r.id}`} />
                  </>
                ) : (
                  <LifecycleButton op="reenable" onClick={() => lifecycle(r, "reenable")} testid={`lawyer-account-reenable-${r.id}`} />
                )
              )}
              {(r.account_status || "active") !== "deleted" && (
                <LifecycleButton op="delete" onClick={() => lifecycle(r, "delete")} testid={`lawyer-account-delete-${r.id}`} />
              )}
            </>
          )}
        </ActionsMenu>
      ),
    },
  ];

  return (
    <>
      <PageHeader testid="admin-lawyers-header" title="Lawyers" subtitle="Verification is automatic; fees come from the assigned level; approve marketplace listing and manage availability." />
      <FilterBar testid="lawyers-filterbar">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder="Search by name, city, specialization…" className="pl-8" data-testid="lawyers-search" />
        </div>
        <Select value={verified} onValueChange={(v) => { setSkip(0); setVerified(v); }}>
          <SelectTrigger className="w-[160px]" data-testid="lawyers-filter-verified"><SelectValue placeholder="Verified" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Verified</SelectItem>
            <SelectItem value="false">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={listing} onValueChange={(v) => { setSkip(0); setListing(v); }}>
          <SelectTrigger className="w-[150px]" data-testid="lawyers-filter-listing"><SelectValue placeholder="Listing" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="listing-filter-all">All listings</SelectItem>
            <SelectItem value="listed" data-testid="listing-filter-listed">Listed</SelectItem>
            <SelectItem value="unlisted" data-testid="listing-filter-unlisted">Unlisted</SelectItem>
          </SelectContent>
        </Select>
        <Select value={account} onValueChange={(v) => { setSkip(0); setAccount(v); }}>
          <SelectTrigger className="w-[170px]" data-testid="lawyers-filter-account"><SelectValue placeholder="Accounts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="exclude_deleted" data-testid="account-filter-exclude-deleted">Hide deleted</SelectItem>
            <SelectItem value="deleted" data-testid="account-filter-deleted">Deleted only</SelectItem>
            <SelectItem value="all" data-testid="account-filter-all">Include deleted</SelectItem>
          </SelectContent>
        </Select>
        <Select value={level} onValueChange={(v) => { setSkip(0); setLevel(v); }}>
          <SelectTrigger className="w-[170px]" data-testid="lawyers-filter-level"><SelectValue placeholder="Level" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any" data-testid="level-filter-any">Level: any</SelectItem>
            <SelectItem value="unassigned" data-testid="level-filter-unassigned">Level: unassigned</SelectItem>
            <SelectItem value="assigned" data-testid="level-filter-assigned">Level: assigned</SelectItem>
          </SelectContent>
        </Select>
      </FilterBar>
      <DataTable testid="lawyers-table" columns={columns} rows={data.items} empty={loading ? "Loading…" : "No lawyers match"} />
      <Pagination testid="lawyers-pagination" total={data.total} skip={skip} limit={LIMIT} onChange={setSkip} />
    </>
  );
}
